<template>
  <section class="rounded-[20px] border border-white/90 bg-white/80 p-5 shadow-[0_16px_48px_rgba(30,64,115,0.055)] backdrop-blur-xl">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 class="text-lg font-semibold text-zinc-900">
          Revenue
        </h3>
        <p class="mt-0.5 text-sm text-zinc-500">
          Processed member payments
        </p>
      </div>
      <div class="inline-flex rounded-lg border border-zinc-200 bg-zinc-50 p-1 text-xs font-semibold text-zinc-600">
        <button
          v-for="option in rangeOptions"
          :key="option.value"
          type="button"
          class="rounded-md px-2.5 py-1.5 transition"
          :class="rangeKey === option.value ? 'bg-[#2D8CFF] text-white shadow-sm' : 'hover:bg-white'"
          @click="rangeKey = option.value"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <div class="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <div
        v-for="metric in summaryMetrics"
        :key="metric.key"
        class="rounded-lg bg-zinc-50 p-3"
      >
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500">
          {{ metric.label }}
        </p>
        <p class="mt-1 text-lg font-semibold text-zinc-900">
          {{ formatUsd(metric.amountCents) }}
        </p>
        <p class="mt-0.5 text-xs text-zinc-500">
          {{ metric.count }} payments
        </p>
      </div>
    </div>

    <div class="mt-5 h-72 overflow-hidden rounded-lg bg-zinc-50 p-4">
      <div
        v-if="isLoading"
        class="flex h-full items-end gap-2"
      >
        <div
          v-for="index in 12"
          :key="`revenue-skeleton-${index}`"
          class="flex-1 animate-pulse rounded-t-md bg-zinc-200"
          :style="{ height: `${24 + ((index * 13) % 54)}%` }"
        />
      </div>
      <div
        v-else-if="errorMessage"
        class="flex h-full items-center justify-center text-sm text-red-600"
      >
        {{ errorMessage }}
      </div>
      <div
        v-else-if="!chartBars.length"
        class="flex h-full items-center justify-center text-sm text-zinc-500"
      >
        No processed payments in this range.
      </div>
      <div v-else class="revenue-scroll h-full overflow-x-auto overflow-y-hidden pb-1">
        <div class="flex h-full items-end gap-2" :style="{ minWidth: chartMinWidth }">
          <div
            v-for="bar in chartBars"
            :key="bar.sortKey || bar.label"
            class="group relative flex h-full min-w-[46px] flex-1 flex-col items-center justify-end gap-2 outline-none"
            tabindex="0"
            :aria-label="`${bar.label}: ${formatUsd(bar.revenueCents)}, ${bar.paymentCount} payments`"
          >
            <div class="revenue-tooltip" role="tooltip">
              <strong>{{ bar.label }}</strong>
              <span>{{ formatUsd(bar.revenueCents) }}</span>
              <small>{{ bar.paymentCount }} payment{{ bar.paymentCount === 1 ? '' : 's' }}</small>
            </div>
            <div class="flex h-44 w-full items-end rounded-md bg-white">
              <div class="w-full rounded-t-md bg-[#2D8CFF] transition-all duration-300 group-hover:bg-[#176fe2] group-focus:bg-[#176fe2]" :style="{ height: `${bar.heightPercent}%` }" />
            </div>
            <div class="w-full truncate text-center text-[11px] font-medium text-zinc-500">{{ bar.displayLabel }}</div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const rangeOptions = [
  { value: 'today', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

const rangeKey = ref('month');
const isLoading = ref(false);
const errorMessage = ref('');
const summary = ref({});
const series = ref([]);

const formatUsd = (cents) => {
  const amount = Number(cents || 0) / 100;
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: amount >= 1000 ? 0 : 2,
  });
};

const summaryMetrics = computed(() => [
  {
    key: 'today',
    label: 'Today',
    amountCents: Number(summary.value?.todayRevenueCents || 0),
    count: Number(summary.value?.todayPaymentCount || 0),
  },
  {
    key: 'week',
    label: 'This Week',
    amountCents: Number(summary.value?.weekRevenueCents || 0),
    count: Number(summary.value?.weekPaymentCount || 0),
  },
  {
    key: 'month',
    label: 'This Month',
    amountCents: Number(summary.value?.monthRevenueCents || 0),
    count: Number(summary.value?.monthPaymentCount || 0),
  },
  {
    key: 'year',
    label: 'This Year',
    amountCents: Number(summary.value?.yearRevenueCents || 0),
    count: Number(summary.value?.yearPaymentCount || 0),
  },
]);

const chartBars = computed(() => {
  const rows = Array.isArray(series.value) ? series.value : [];
  const max = Math.max(...rows.map((row) => Number(row?.revenueCents || 0)), 0);
  return rows.map((row) => {
    const revenueCents = Number(row?.revenueCents || 0);
    return {
      label: String(row?.label || '').trim(),
      displayLabel: rangeKey.value === 'month'
        ? String(row?.label || '').trim().replace(/^[A-Za-z]{3}\s+/, '')
        : String(row?.label || '').trim(),
      sortKey: String(row?.sortKey || '').trim(),
      revenueCents,
      paymentCount: Number(row?.paymentCount || 0),
      heightPercent: max > 0 && revenueCents > 0 ? Math.max(6, Math.round((revenueCents / max) * 100)) : 0,
    };
  });
});

const chartMinWidth = computed(() => {
  const perBar = rangeKey.value === 'month' ? 58 : (rangeKey.value === 'today' ? 64 : 52);
  return `${Math.max(620, chartBars.value.length * perBar)}px`;
});

async function loadRevenueSummary() {
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const response = await fetch(
      `${apiProxyUrl}/api/creator-payments/creator/revenue-summary?rangeKey=${encodeURIComponent(rangeKey.value)}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || data?.error || 'Failed to load revenue');
    }
    summary.value = data?.summary || {};
    series.value = Array.isArray(data?.series) ? data.series : [];
  } catch (error) {
    errorMessage.value = error?.message || 'Failed to load revenue';
    summary.value = {};
    series.value = [];
  } finally {
    isLoading.value = false;
  }
}

watch(rangeKey, loadRevenueSummary);
onMounted(loadRevenueSummary);
</script>

<style scoped>
.revenue-scroll { scrollbar-width: thin; scrollbar-color: #a1a1aa transparent; }
.revenue-scroll::-webkit-scrollbar { height: 6px; }
.revenue-scroll::-webkit-scrollbar-thumb { border-radius: 999px; background: #a1a1aa; }
.revenue-tooltip { position: absolute; left: 50%; top: 0; z-index: 20; width: max-content; min-width: 112px; transform: translate(-50%, 6px); border: 1px solid #27272a; border-radius: 10px; background: #18181b; padding: 8px 10px; color: white; box-shadow: 0 12px 28px rgba(24,24,27,.2); opacity: 0; pointer-events: none; transition: opacity .16s ease, transform .16s ease; }
.revenue-tooltip::after { content: ''; position: absolute; left: 50%; top: 100%; width: 8px; height: 8px; transform: translate(-50%, -4px) rotate(45deg); background: #18181b; border-right: 1px solid #27272a; border-bottom: 1px solid #27272a; }
.revenue-tooltip strong,.revenue-tooltip span,.revenue-tooltip small { display: block; }
.revenue-tooltip strong { color: #d4d4d8; font-size: 10px; font-weight: 600; }
.revenue-tooltip span { margin-top: 2px; font-size: 13px; font-weight: 700; }
.revenue-tooltip small { margin-top: 2px; color: #a1a1aa; font-size: 10px; }
.group:hover .revenue-tooltip,.group:focus .revenue-tooltip { transform: translate(-50%, 0); opacity: 1; }
</style>
